import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  DirectionsCar as DirectionsCarIcon,
  Edit as EditIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { API_ENDPOINTS } from '../config/api';

const emptyDraft = { model_name: '', sort_order: 0, is_active: true };

const VehicleModels = () => {
  const [models, setModels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(emptyDraft);

  const loadModels = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch(API_ENDPOINTS.VEHICLE_MODELS);
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Could not load vehicle models');
      setModels(data.models || []);
    } catch (loadError) {
      setError(loadError.message || 'Could not load vehicle models');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadModels(); }, [loadModels]);

  const save = async (path, method, draft) => {
    setIsSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await fetch(`${API_ENDPOINTS.VEHICLE_MODELS}${path}`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(draft)
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Could not save vehicle model');
      await loadModels();
      setNotice(method === 'POST' ? 'Vehicle model added.' : 'Vehicle model updated.');
      return true;
    } catch (saveError) {
      setError(saveError.message || 'Could not save vehicle model');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const createModel = async () => {
    const created = await save('', 'POST', createDraft);
    if (created) {
      setCreateOpen(false);
      setCreateDraft(emptyDraft);
    }
  };

  const startEdit = (model) => {
    setEditingId(model.id);
    setEditDraft({
      model_name: model.model_name,
      sort_order: model.sort_order,
      is_active: model.is_active
    });
  };

  const updateModel = async (id) => {
    const updated = await save(`/${id}`, 'PUT', editDraft);
    if (updated) setEditingId(null);
  };

  const deleteModel = async (model) => {
    if (!window.confirm(`Delete “${model.model_name}”?`)) return;
    setIsSaving(true);
    setError('');
    setNotice('');
    try {
      const response = await fetch(`${API_ENDPOINTS.VEHICLE_MODELS}/${model.id}`, { method: 'DELETE' });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Could not delete vehicle model');
      await loadModels();
      setNotice('Vehicle model deleted.');
    } catch (deleteError) {
      setError(deleteError.message || 'Could not delete vehicle model');
    } finally {
      setIsSaving(false);
    }
  };

  const ModelFields = ({ draft, onChange }) => (
    <Stack spacing={2} sx={{ pt: 0.5 }}>
      <TextField
        autoFocus
        required
        fullWidth
        label="Model name"
        placeholder="e.g. BYD Seal"
        value={draft.model_name}
        onChange={(event) => onChange({ ...draft, model_name: event.target.value })}
        inputProps={{ maxLength: 160 }}
      />
      <TextField
        fullWidth
        label="Display order"
        type="number"
        helperText="Lower numbers appear first in the XHS model list."
        value={draft.sort_order}
        onChange={(event) => onChange({ ...draft, sort_order: event.target.value })}
      />
      <Stack direction="row" alignItems="center" spacing={1}>
        <Switch
          checked={draft.is_active}
          onChange={(event) => onChange({ ...draft, is_active: event.target.checked })}
        />
        <Typography variant="body2">Active — show this model in the XHS generator</Typography>
      </Stack>
    </Stack>
  );

  return (
    <Box sx={{ maxWidth: 1160, mx: 'auto', pb: 5 }}>
      <Card sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 12px 34px rgba(44, 62, 80, 0.12)' }}>
        <Box sx={{ p: { xs: 2.5, sm: 4 }, color: 'white', background: 'linear-gradient(120deg, #243b55 0%, #3498db 100%)' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ width: 54, height: 54, display: 'grid', placeItems: 'center', borderRadius: 3, bgcolor: 'rgba(255,255,255,0.18)' }}>
                <DirectionsCarIcon fontSize="large" />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '1.7rem', sm: '2.15rem' } }}>Vehicle Models</Typography>
                <Typography sx={{ opacity: 0.9 }}>Manage the models shown under “车型号” in the XHS generator.</Typography>
              </Box>
            </Stack>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)} sx={{ bgcolor: 'white', color: '#24527a', fontWeight: 800, '&:hover': { bgcolor: '#f4f8fb' } }}>
              Add model
            </Button>
          </Stack>
        </Box>

        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            These records are linked to business ID, not a hardcoded brand name. Inactive models remain saved but are hidden from customers.
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

          {isLoading ? (
            <Box sx={{ py: 7, textAlign: 'center' }}><CircularProgress /></Box>
          ) : models.length === 0 ? (
            <Box sx={{ py: 7, textAlign: 'center', border: '1px dashed #c9d4df', borderRadius: 3, bgcolor: '#f8fafc' }}>
              <DirectionsCarIcon sx={{ fontSize: 44, color: '#78909c', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>No vehicle models yet</Typography>
              <Typography color="text.secondary">Add models such as BYD Seal or DENZA D9 to make them available in XHS.</Typography>
            </Box>
          ) : (
            <Stack spacing={1.25}>
              {models.map((model) => {
                const isEditing = editingId === model.id;
                return (
                  <Card key={model.id} variant="outlined" sx={{ borderRadius: 2.5, borderColor: isEditing ? '#3498db' : '#e1e8ed' }}>
                    <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
                      {isEditing ? (
                        <>
                          <ModelFields draft={editDraft} onChange={setEditDraft} />
                          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                            <Button onClick={() => setEditingId(null)} disabled={isSaving}>Cancel</Button>
                            <Button variant="contained" startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} onClick={() => updateModel(model.id)} disabled={isSaving}>Save</Button>
                          </Stack>
                        </>
                      ) : (
                        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1.5}>
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="h6" sx={{ fontWeight: 750 }}>{model.model_name}</Typography>
                              <Chip size="small" label={model.is_active ? 'Active' : 'Hidden'} color={model.is_active ? 'success' : 'default'} variant="outlined" />
                            </Stack>
                            <Typography variant="caption" color="text.secondary">Display order: {model.sort_order}</Typography>
                          </Box>
                          <Stack direction="row" spacing={0.5}>
                            <Button size="small" startIcon={<EditIcon />} onClick={() => startEdit(model)} disabled={isSaving}>Edit</Button>
                            <IconButton aria-label={`Delete ${model.model_name}`} color="error" onClick={() => deleteModel(model)} disabled={isSaving}><DeleteIcon /></IconButton>
                          </Stack>
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onClose={() => !isSaving && setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add vehicle model</DialogTitle>
        <DialogContent><ModelFields draft={createDraft} onChange={setCreateDraft} /></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCreateOpen(false)} disabled={isSaving}>Cancel</Button>
          <Button variant="contained" startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />} onClick={createModel} disabled={isSaving || !createDraft.model_name.trim()}>Add model</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VehicleModels;
